import { registerTaxIdValidator } from "./tax-id.js";
/**
 * The structure of every EU VAT identification number, as VIES publishes it
 * ("VAT identification number structure", FAQ question 11, read off the
 * portal's translation service on 2026-09-10). One country per line, so that
 * adding a member state is a row here and not an edit to the parser — the same
 * shape `nip.ts` set for Poland.
 *
 * **Structure only, never a checksum.** Most member states do have one, and the
 * Commission's own answer is that it "cannot divulge these algorithms". The
 * ones that are public vary per state, and a wrong one would reject a real
 * customer at the boundary with a `400` — a fail-closed outcome with no finding
 * recorded and nothing for support to read. What the structure catches is the
 * typo VIES would otherwise answer `valid: false` for: a wrong length, a letter
 * where a digit goes. The register decides the rest.
 *
 * **The country is the VAT prefix, which is ISO 3166-1 alpha-2 except twice**:
 * Greece issues `EL`, not `GR`, and Northern Ireland trades under `XI` since
 * 2021. Both are what a customer prints on an invoice and what VIES accepts —
 * measured 2026-09-10, `GR` answers `INVALID_INPUT` — so both are the key here,
 * and `GR` is deliberately not aliased: a Greek number written with `GR` parses
 * as a country no source covers and prepays under `uncoveredCountryPolicy`,
 * which is visible in the audit log rather than silently rewritten.
 *
 * Legend from the FAQ: `9` a digit, `X` a letter or a digit, `S` a letter, a
 * digit, `+` or `*`, `L` a letter. `parseTaxId` has already stripped spaces,
 * dots and dashes and upper-cased the rest before these run.
 */
export const EU_VAT_FORMATS = new Map([
    /** `ATU99999999` — the character after the prefix is always `U`. */
    ["AT", /^U\d{8}$/],
    /** `BE0999999999` / `BE1999999999` — ten digits, the first `0` or `1`. */
    ["BE", /^[01]\d{9}$/],
    ["BG", /^\d{9,10}$/],
    /** `CY99999999L`. */
    ["CY", /^\d{8}[A-Z]$/],
    ["CZ", /^\d{8,10}$/],
    ["DE", /^\d{9}$/],
    ["DK", /^\d{8}$/],
    ["EE", /^\d{9}$/],
    /** Greece. `EL`, never `GR` — see above. */
    ["EL", /^\d{9}$/],
    /** `ESX9999999X` — first and last may be alpha or numeric, but not both numeric. */
    ["ES", /^(?!\d{9}$)[A-Z0-9]\d{7}[A-Z0-9]$/],
    ["FI", /^\d{8}$/],
    /** `FRXX999999999` — two check characters, letters or digits, then nine digits. */
    ["FR", /^[A-Z0-9]{2}\d{9}$/],
    ["HR", /^\d{11}$/],
    ["HU", /^\d{8}$/],
    /** `IE9S99999L` (the second character may also be `+` or `*`) or `IE9999999LL`. */
    ["IE", /^(\d[A-Z0-9+*]\d{5}[A-Z]|\d{7}[A-Z]{2})$/],
    ["IT", /^\d{11}$/],
    ["LT", /^(\d{9}|\d{12})$/],
    ["LU", /^\d{8}$/],
    ["LV", /^\d{11}$/],
    ["MT", /^\d{8}$/],
    /** `NLSSSSSSSSSSSS` — twelve characters from letters, digits, `+` and `*`. */
    ["NL", /^[A-Z0-9+*]{12}$/],
    /** Poland is `nip.ts`, with its checksum. Listed so the member-state set is complete. */
    ["PL", /^\d{10}$/],
    ["PT", /^\d{9}$/],
    ["RO", /^\d{2,10}$/],
    ["SE", /^\d{12}$/],
    ["SI", /^\d{8}$/],
    ["SK", /^\d{10}$/],
    /** Northern Ireland: `XI999999999`, `XI999999999999`, `XIGD999` or `XIHA999`. */
    ["XI", /^(\d{9}|\d{12}|GD\d{3}|HA\d{3})$/],
]);
/**
 * Every prefix VIES answers for — the 27 member states plus Northern Ireland,
 * in VAT-prefix spelling. Measured against `GET /check-status` on 2026-09-10:
 * 28 codes, this set exactly. The provider that reads VIES derives its
 * coverage from this list, so there is one list rather than two that drift.
 */
export const EU_VAT_COUNTRIES = [...EU_VAT_FORMATS.keys()];
export function isEuVatCountry(country) {
    return EU_VAT_FORMATS.has(country.toUpperCase());
}
/**
 * Registered on import, like the Polish checksum. **Poland keeps its own
 * validator**: the checksum is stricter than the structure, and registering the
 * structure over it would loosen the boundary for the one country that is
 * actually served today.
 */
for (const [country, format] of EU_VAT_FORMATS) {
    if (country === "PL")
        continue;
    registerTaxIdValidator(country, (value) => format.test(value));
}
//# sourceMappingURL=eu-vat.js.map